import apiClient from './client';
import type { CreateSongPayload, PublicSongListItem, Song, SongListItem, Team, UpdateSongPayload } from '../types/song';

export interface TeamContext {
  team_id: string;
  team_name: string;
  member_count: number;
  owner_id: string;
  owner_name: string;
  owner_plan_name: string;
  user_role: 'ADMIN' | 'LEADER' | 'EDITOR' | 'MEMBER';
  joined_at: string;
}

export const teamsApi = {
  list: () => apiClient.get<Team[]>('/teams/'),
  create: (name: string, ministryId: string) => apiClient.post<Team>('/teams/', { name, ministry_id: ministryId }),
  getContexts: () => apiClient.get<TeamContext[]>('/teams/me/'),
};

export const songsApi = {
  list: (ministryId: string, params?: { search?: string; key?: string; limit?: number; offset?: number }) =>
    apiClient.get<{ count: number; results: SongListItem[] }>(`/ministries/${ministryId}/songs/`, { params }),

  listPublic: (ministryId: string, params?: { search?: string; limit?: number; offset?: number }) =>
    apiClient.get<{ count: number; results: PublicSongListItem[] }>(`/ministries/${ministryId}/songs/public/`, { params }),

  savePublic: (ministryId: string, songId: string) =>
    apiClient.post<Song>(`/ministries/${ministryId}/songs/from-public/`, { song_id: songId }),

  get: (ministryId: string, songId: string) =>
    apiClient.get<Song>(`/ministries/${ministryId}/songs/${songId}/`),

  create: (ministryId: string, payload: CreateSongPayload) =>
    apiClient.post<Song>(`/ministries/${ministryId}/songs/`, payload),

  update: (ministryId: string, songId: string, payload: UpdateSongPayload) =>
    apiClient.patch<Song>(`/ministries/${ministryId}/songs/${songId}/`, payload),

  delete: (ministryId: string, songId: string) =>
    apiClient.delete(`/ministries/${ministryId}/songs/${songId}/`),

  deletePermanently: (ministryId: string, songId: string) =>
    apiClient.delete(`/ministries/${ministryId}/songs/${songId}/permanent/`),

  share: (ministryId: string, songId: string, targetMinistryId: string) =>
    apiClient.post<{ shared_ministry_ids: string[] }>(
      `/ministries/${ministryId}/songs/${songId}/sharing/`,
      { ministry_id: targetMinistryId },
    ),

  unshare: (ministryId: string, songId: string, targetMinistryId: string) =>
    apiClient.delete<{ shared_ministry_ids: string[] }>(
      `/ministries/${ministryId}/songs/${songId}/sharing/`,
      { data: { ministry_id: targetMinistryId } },
    ),

  transpose: (ministryId: string, songId: string, payload: { target_key?: string; semitones?: number }) =>
    apiClient.post<Song>(`/ministries/${ministryId}/songs/${songId}/transpose/`, payload),

  detectKey: (ministryId: string, songId: string) =>
    apiClient.post<{ detected_key: string | null }>(`/ministries/${ministryId}/songs/${songId}/detect-key/`, {}),
};
